import React, { useCallback, useMemo, useState } from "react";
import { ToastContext } from "./ToastContext";
import { ToastGroup } from "./ToastGroup";
import type { ToastProviderProps, ToastData, ToastOptions } from "./types";

let toastIdCounter = 0;

const generateToastId = (): string => {
  return `toast-${++toastIdCounter}-${Date.now()}`;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top",
  align = "center",
  defaultDuration = 5000,
  maxWidth,
  testID,
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const addToast = useCallback(
    (options: ToastOptions): string => {
      const id = generateToastId();
      const duration = options.duration ?? defaultDuration;

      const newToast: ToastData = {
        id,
        type: options.type ?? "neutral",
        title: options.title,
        description: options.description,
        descriptionLines: options.descriptionLines,
        icon: options.icon,
        action: options.action,
        showCloseButton: options.showCloseButton,
        progress: options.progress,
        duration,
      };

      setToasts((prev) => [...prev, newToast]);

      return id;
    },
    [defaultDuration]
  );

  const contextValue = useMemo(
    () => ({
      toasts,
      addToast,
      dismissToast,
      dismissAllToasts,
    }),
    [toasts, addToast, dismissToast, dismissAllToasts]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastGroup
        toasts={toasts}
        position={position}
        align={align}
        maxWidth={maxWidth}
        onDismiss={dismissToast}
        testID={testID}
      />
    </ToastContext.Provider>
  );
};
